import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'

import styled from '@emotion/styled'
import { Button, Props as ButtonProps } from 'app/components/Common/Button'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { uniq } from 'lodash'
import React, { memo } from 'react'
import Fade from 'react-reveal/Fade'
import Slider from 'react-slick'

export interface Props {
  ctas?: ButtonProps[]
  description?: string
  images?: ImageProps[]
  isOdd?: boolean
  subtitle?: string
  title?: string
  whatsappLabel?: string
  whatsappNumber?: string
}

export const Stripe = memo(function Stripe({
  ctas,
  description,
  images,
  isOdd,
  subtitle,
  title,
  whatsappLabel,
  whatsappNumber,
}: Props) {
  const settings = {
    autoplay: true,
    autoplaySpeed: 4000,
    fade: true,
    infinite: true,
    speed: 2000,
  }

  return (
    <Container>
      <Fade
        right={isOdd ? false : true}
        left={isOdd ? true : false}
        distance="7.5rem"
      >
        <Wrapper className={!isOdd ? 'stripe-even' : undefined}>
          {title ? <Title>{title}</Title> : null}
          {subtitle ? <Subtitle>{subtitle}</Subtitle> : null}
          {description ? (
            <Description dangerouslySetInnerHTML={{ __html: description }} />
          ) : null}
          {ctas
            ? ctas.map((item, index) => (
                <CTA iconArrowRight variant="S" key={index} {...item} />
              ))
            : null}
          {whatsappNumber ? (
            <CTA
              label={whatsappLabel || ''}
              rel="noopener"
              target="_blank"
              URL={`https://api.whatsapp.com/send?phone=${whatsappNumber}`}
            />
          ) : null}
        </Wrapper>
      </Fade>
      {images ? (
        <Carousel>
          <Slider lazyLoad="progressive" {...settings}>
            {uniq(images).map((item, index) => (
              <Image key={index} {...item} />
            ))}
          </Slider>
        </Carousel>
      ) : null}
    </Container>
  )
})

const Container = styled.div`
  padding: 4.625rem 0;
  position: relative;
  .react-reveal {
    position: relative;
    z-index: 4;
  }

  @media (max-width: 1023px) {
    display: flex;
    flex-wrap: wrap;
    padding: 0;
    > div {
      width: 100%;
      &:first-of-type {
        order: 2;
        animation: none !important;
      }
    }
  }
`

const Wrapper = styled.div`
  width: 50%;
  background: ${({ theme }) => theme.colors.variants.neutralLight3};
  padding: 4.875rem 6.806vw 4.875rem 10vw;
  &.stripe-even {
    margin-left: auto;
    padding-right: 10vw;
    padding-left: 6.806vw;
  }

  @media (max-width: 1199px) {
    padding: 3.75rem 6.806vw 60px 8.454vw;
    &.stripe-even {
      padding-right: 8.454vw;
      padding-left: 6.806vw;
    }
  }

  @media (max-width: 1023px) {
    width: 100%;
    background: ${({ theme }) => theme.colors.variants.neutralLight2};
    padding: 2.5rem 8.454vw;
    &.stripe-even {
      padding: 2.5rem 8.454vw;
    }
  }
`

const Title = styled.h2`
  color: ${({ theme }) => theme.colors.variants.neutralDark1};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 2rem;
  line-height: 2.375rem;
  margin-bottom: 0.625rem;
`

const Subtitle = styled.h3`
  color: ${({ theme }) => theme.colors.variants.neutralLight1};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 1.375rem;
  line-height: 1.875rem;
`

const Description = styled.div`
  color: ${({ theme }) => theme.colors.variants.neutralDark3};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 0.9375rem;
  line-height: 1.625rem;
  margin-top: 2.5rem;
`

const CTA = styled(Button)`
  svg {
    opacity: 0.5;
    stroke: ${({ theme }) => theme.colors.variants.neutralLight4};
  }
`

const Carousel = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: absolute;
  top: 0;
  left: 0;
  .slick-slider {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    &:before {
      content: '';
      width: 100%;
      height: 100%;
      background: ${({ theme }) => theme.colors.variants.neutralDark1};
      opacity: 0.2;
      position: absolute;
      top: 0;
      left: 0;
      z-index: 3;
    }
    .slick-list,
    .slick-track {
      height: 100%;
      z-index: 2;
    }
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      position: absolute;
      top: 0;
      left: 0;
    }
  }

  @media (max-width: 1023px) {
    padding-bottom: 58%;
    position: relative;
    top: auto;
    left: auto;
  }
`
